import React, { useState, useEffect, useRef } from 'react';
import { Artist, CreateArtistRequest, UpdateArtistRequest } from '../../types/artist';
import { artistApi } from '../../services/artistApi';
import { useAuth } from '../../contexts/AuthContext';
import { FormGroup, FormLabel, FormInput, FormTextarea, FormButton } from './FormComponents';
import './ArtistForm.css';

interface ArtistFormProps {
  artist?: Artist;
  onSave: (artist: Artist) => void;
  onCancel: () => void;
}

export const ArtistForm: React.FC<ArtistFormProps> = ({ artist, onSave, onCancel }) => {
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [soundcloudUrl, setSoundcloudUrl] = useState('');
  const [bandcampUrl, setBandcampUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [tikTokUrl, setTikTokUrl] = useState('');
  const [linktreeUrl, setLinktreeUrl] = useState('');
  const [usesAI, setUsesAI] = useState(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [deleteExistingImage, setDeleteExistingImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (artist) {
      setName(artist.name);
      setBio(artist.bio || '');
      setCountry(artist.country || '');
      setState(artist.state || '');
      setCity(artist.city || '');
      setSoundcloudUrl(artist.soundcloudUrl || '');
      setBandcampUrl(artist.bandcampUrl || '');
      setInstagramUrl(artist.instagramUrl || '');
      setYoutubeUrl(artist.youtubeUrl || '');
      setTikTokUrl(artist.tikTokUrl || '');
      setLinktreeUrl(artist.linktreeUrl || '');
      setUsesAI(artist.usesAI || false);
      
      // Set existing profile image preview
      if (artist.profileImageUrl) {
        setProfileImagePreview(artist.profileImageUrl);
      }
    }
  }, [artist]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      
      setProfileImage(file);
      setDeleteExistingImage(false);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      if (error) setError(null);
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setProfileImagePreview(null);
    setDeleteExistingImage(artist?.profileImageUrl ? true : false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || !user) return;

    if (!name.trim()) {
      setError('Artist name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const artistData: CreateArtistRequest | UpdateArtistRequest = {
        name: name.trim(),
        bio: bio.trim() || null,
        country: country.trim() || null,
        state: state.trim() || null,
        city: city.trim() || null,
        soundcloudUrl: soundcloudUrl.trim() || null,
        bandcampUrl: bandcampUrl.trim() || null,
        instagramUrl: instagramUrl.trim() || null,
        youtubeUrl: youtubeUrl.trim() || null,
        tikTokUrl: tikTokUrl.trim() || null,
        linktreeUrl: linktreeUrl.trim() || null,
        usesAI: usesAI,
      };

      let savedArtist: Artist;

      if (artist && artist.id) {
        // Update existing artist
        await artistApi.updateArtist(artist.id, artistData);
        
        // Handle profile image separately
        if (deleteExistingImage && artist.profileImageUrl) {
          await artistApi.deleteProfileImage(artist.id);
        }
        
        if (profileImage) {
          savedArtist = await artistApi.uploadProfileImage(artist.id, profileImage);
        } else {
          // Fetch updated artist data
          savedArtist = await artistApi.getArtistById(artist.id);
        }
      } else {
        // Create new artist
        savedArtist = await artistApi.createArtist(artistData);
        
        // Upload profile image if provided
        if (profileImage && savedArtist.id) {
          savedArtist = await artistApi.uploadProfileImage(savedArtist.id, profileImage);
        }
      }

      onSave(savedArtist);
    } catch (error: any) {
      setError(error.message || 'Failed to save artist');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (error) setError(null);
  };

  return (
    <div className="artist-form-container">
      <h3 className="artist-form-title">
        {artist ? 'Edit Artist Profile' : 'Create Artist Profile'}
      </h3>
      
      {error && (
        <div className="artist-error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="artist-form">
        {/* Profile Image */}
        <FormGroup>
          <FormLabel>Profile Image</FormLabel>
          <div className="profile-image-upload">
            {profileImagePreview ? (
              <div className="profile-image-preview-container">
                <img 
                  src={profileImagePreview} 
                  alt="Profile preview" 
                  className="profile-image-preview"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="remove-image-button"
                  disabled={isSubmitting}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="profile-image-placeholder">
                <span>No image selected</span>
              </div>
            )}
            <input
              type="file"
              id="profileImage"
              ref={fileInputRef}
              onChange={handleImageSelect}
              accept="image/*"
              style={{ display: 'none' }}
              disabled={isSubmitting}
            />
            <FormButton
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting}
              variant="secondary"
            >
              {profileImagePreview ? 'Change Image' : 'Choose Image'}
            </FormButton>
            <small className="form-help">
              Upload a profile image (JPG, PNG, or GIF, max 5MB)
            </small>
          </div>
        </FormGroup>

        {/* Artist Name */}
        <FormGroup>
          <FormLabel htmlFor="artistName">
            Artist Name <span className="required">*</span>
          </FormLabel>
          <FormInput
            type="text"
            id="artistName"
            value={name}
            onChange={handleNameChange}
            required
            disabled={isSubmitting}
            placeholder="Enter your artist name"
            maxLength={200}
          />
          <small className="form-help">
            This is the name that will be displayed on your tracks and feedback
          </small>
        </FormGroup>

        {/* Bio */}
        <FormGroup>
          <FormLabel htmlFor="bio">
            Bio
          </FormLabel>
          <FormTextarea
            id="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            disabled={isSubmitting}
            placeholder="Tell us about yourself and your music"
            maxLength={1000}
            rows={4}
          />
          <small className="form-help">
            Share your musical background, influences, or what you're working on
          </small>
        </FormGroup>

        {/* AI Usage */}
        <FormGroup>
          <FormLabel>
            Do you use AI in your music production?
          </FormLabel>
          <div className="radio-button-group">
            <label className={`radio-button ${usesAI === true ? 'selected' : ''}`}>
              <input
                type="radio"
                name="usesAI"
                value="yes"
                checked={usesAI === true}
                onChange={() => setUsesAI(true)}
                disabled={isSubmitting}
              />
              <span>Yes</span>
            </label>
            <label className={`radio-button ${usesAI === false ? 'selected' : ''}`}>
              <input
                type="radio"
                name="usesAI"
                value="no"
                checked={usesAI === false}
                onChange={() => setUsesAI(false)}
                disabled={isSubmitting}
              />
              <span>No</span>
            </label>
          </div>
          <small className="form-help">
            Select "Yes" if you use AI tools for composing, generating, or producing your music
          </small>
        </FormGroup>

        {/* Location Section */}
        <div className="form-section">
          <h4 className="form-section-title">Location</h4>
          
          <div className="form-row">
            <FormGroup className="form-group-half">
              <FormLabel htmlFor="country">
                Country
              </FormLabel>
              <FormInput
                type="text"
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={isSubmitting}
                placeholder="e.g., United States"
                maxLength={100}
              />
            </FormGroup>

            <FormGroup className="form-group-half">
              <FormLabel htmlFor="state">
                State/Province
              </FormLabel>
              <FormInput
                type="text"
                id="state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                disabled={isSubmitting}
                placeholder="e.g., California"
                maxLength={100}
              />
            </FormGroup>
          </div>

          <FormGroup>
            <FormLabel htmlFor="city">
              City
            </FormLabel>
            <FormInput
              type="text"
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={isSubmitting}
              placeholder="e.g., Los Angeles"
              maxLength={100}
            />
          </FormGroup>
        </div>

        {/* Social Links Section */}
        <div className="form-section">
          <h4 className="form-section-title">Social Links</h4>
          
          <FormGroup>
            <FormLabel htmlFor="soundcloudUrl">
              SoundCloud
            </FormLabel>
            <FormInput
              type="url"
              id="soundcloudUrl"
              value={soundcloudUrl}
              onChange={(e) => setSoundcloudUrl(e.target.value)}
              disabled={isSubmitting}
              placeholder="https://soundcloud.com/yourprofile"
              maxLength={500}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="bandcampUrl">
              Bandcamp
            </FormLabel>
            <FormInput
              type="url"
              id="bandcampUrl"
              value={bandcampUrl}
              onChange={(e) => setBandcampUrl(e.target.value)}
              disabled={isSubmitting}
              placeholder="https://yourname.bandcamp.com"
              maxLength={500}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="instagramUrl">
              Instagram
            </FormLabel>
            <FormInput
              type="url"
              id="instagramUrl"
              value={instagramUrl}
              onChange={(e) => setInstagramUrl(e.target.value)}
              disabled={isSubmitting}
              placeholder="https://instagram.com/yourprofile"
              maxLength={500}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="youtubeUrl">
              YouTube
            </FormLabel>
            <FormInput
              type="url"
              id="youtubeUrl"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              disabled={isSubmitting}
              placeholder="https://youtube.com/@yourprofile"
              maxLength={500}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="tikTokUrl">
              TikTok
            </FormLabel>
            <FormInput
              type="url"
              id="tikTokUrl"
              value={tikTokUrl}
              onChange={(e) => setTikTokUrl(e.target.value)}
              disabled={isSubmitting}
              placeholder="https://tiktok.com/@yourprofile"
              maxLength={500}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="linktreeUrl">
              Linktree
            </FormLabel>
            <FormInput
              type="url"
              id="linktreeUrl"
              value={linktreeUrl}
              onChange={(e) => setLinktreeUrl(e.target.value)}
              disabled={isSubmitting}
              placeholder="https://linktr.ee/yourprofile"
              maxLength={500}
            />
          </FormGroup>
        </div>

        <div className="artist-form-actions">
          <FormButton
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            variant="secondary"
          >
            Cancel
          </FormButton>
          <FormButton
            type="submit"
            disabled={isSubmitting || !name.trim()}
            variant="primary"
          >
            {isSubmitting 
              ? (artist ? 'Updating...' : 'Creating...') 
              : (artist ? 'Update Profile' : 'Create Profile')
            }
          </FormButton>
        </div>
      </form>
    </div>
  );
};